import {
  Big,
  bigSum,
  DailyCountItemStatus,
  DailyCountList,
  defaultImage,
  formatCurrency,
  InventoryChange,
  InventoryChangeReason,
  StoreProduct,
} from '@bofrak-backend/shared';
import {
  Badge,
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Icon,
  Image,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { formatDate } from '@bofrak-backend/shared-i18n';
import { useMemo, useState } from 'react';
import { FaChevronDown, FaChevronRight, FaHistory } from 'react-icons/fa';
import { InventoryBreakdown } from '../../../ui/general/InventoryBreakdown';
import { formatInventoryBreakdown } from '../../utils/format-quantity-sold';
import { useSharedUiTranslation, type SharedUiT } from '../../../i18n';
import { countValueOf } from '../../utils/inventory-gain-split';

interface InventoryGainLossDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  inventoryChanges: InventoryChange[];
  storeProducts: StoreProduct[];
  dailyCountLists?: DailyCountList[];
  dateRange?: { start: Date | null; end: Date | null };
}

const EXCLUDED_REASONS = new Set<InventoryChangeReason>([
  InventoryChangeReason.SALE,
  InventoryChangeReason.REFUND,
  InventoryChangeReason.PURCHASE_ORDER,
]);

const getReasonLabel = (t: SharedUiT): Record<string, string> => ({
  [InventoryChangeReason.INVENTORY_COUNT]: t('Counts'),
  [InventoryChangeReason.INVENTORY_CORRECTION]: t('Corrections'),
  [InventoryChangeReason.PURCHASE_ORDER]: t('Purchases'),
  [InventoryChangeReason.INVENTORY_TRANSFER_IN]: t('Transfers In'),
  [InventoryChangeReason.INVENTORY_TRANSFER_OUT]: t('Transfers Out'),
  [InventoryChangeReason.SALE]: t('Sales'),
  [InventoryChangeReason.REFUND]: t('Refunds'),
});

// Net of valuation adjustments, like the breakdown row it opens from.
const changeValue = (c: InventoryChange): number => countValueOf(c);

export function InventoryGainLossDrawer({
  isOpen,
  onClose,
  inventoryChanges,
  storeProducts,
  dailyCountLists,
}: InventoryGainLossDrawerProps) {
  const { t } = useSharedUiTranslation();
  const storeProductMap = useMemo(() => {
    const map = new Map<string, StoreProduct>();
    for (const p of storeProducts) {
      map.set(p.id, p);
      for (const child of p.store_product_fractions ?? []) {
        map.set(child.id, p);
      }
      for (const frac of p.product_fractions ?? []) {
        if (!map.has(frac.fraction_id)) {
          map.set(frac.fraction_id, p);
        }
      }
    }
    return map;
  }, [storeProducts]);

  const filtered = useMemo(
    () =>
      inventoryChanges
        .filter((c) => !EXCLUDED_REASONS.has(c.reason))
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        ),
    [inventoryChanges],
  );

  const reasonSummaries = useMemo(() => {
    const map = new Map<
      string,
      { label: string; count: number; value: number }
    >();
    const reasonLabel = getReasonLabel(t);
    for (const c of filtered) {
      const key = c.reason;
      const prev = map.get(key) ?? {
        label: reasonLabel[key] ?? key,
        count: 0,
        value: 0,
      };
      prev.count += 1;
      prev.value = new Big(prev.value).plus(changeValue(c)).toNumber();
      map.set(key, prev);
    }
    return [...map.values()];
  }, [filtered, t]);

  const totalValue = useMemo(
    () => bigSum(filtered.map((c) => changeValue(c))).toNumber(),
    [filtered],
  );

  const hasDailyCounts = !!dailyCountLists && dailyCountLists.length > 0;

  const productGroups = useMemo(() => {
    const map = new Map<
      string,
      {
        product_id: string;
        product_name: string;
        totalQty: number;
        totalValue: number;
        changes: InventoryChange[];
      }
    >();
    for (const c of filtered) {
      const prev = map.get(c.product_id);
      if (prev) {
        prev.totalQty = new Big(prev.totalQty)
          .plus(c.change_quantity)
          .toNumber();
        prev.totalValue = new Big(prev.totalValue)
          .plus(changeValue(c))
          .toNumber();
        prev.changes.push(c);
      } else {
        map.set(c.product_id, {
          product_id: c.product_id,
          product_name: c.product_name,
          totalQty: c.change_quantity,
          totalValue: changeValue(c),
          changes: [c],
        });
      }
    }
    return [...map.values()]
      .sort((a, b) => Math.abs(b.totalValue) - Math.abs(a.totalValue))
      .map((g) => ({
        ...g,
        changes: g.changes.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        ),
      }));
  }, [filtered]);

  const [expandedProductId, setExpandedProductId] = useState<string | null>(
    null,
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader pb={1} px={3}>
          <Text fontSize="md" fontWeight="bold">
            {t('Inventory Counts/Corrections')}
          </Text>
        </DrawerHeader>

        <DrawerBody px={0} pb={6}>
          <Tabs size="sm" variant="enclosed" colorScheme="teal" isLazy>
            <TabList px={3}>
              <Tab fontSize="xs">{t('Counts/Corrections')}</Tab>
              {hasDailyCounts && <Tab fontSize="xs">{t('Daily Counts')}</Tab>}
            </TabList>
            <TabPanels>
              {/* ── Tab 1: Existing Counts/Corrections ── */}
              <TabPanel px={3} py={2}>
                {/* Per-reason summary */}
                <VStack spacing={1} mb={2}>
                  {reasonSummaries.map((r) => (
                    <HStack
                      width={'full'}
                      key={r.label}
                      bg="gray.50"
                      px={2}
                      py={1}
                      borderRadius="md">
                      <Text fontSize="2xs" color="gray.500" fontWeight="medium">
                        {r.label}
                      </Text>
                      <Spacer />
                      <HStack spacing={1} align="baseline">
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color={
                            r.value > 0
                              ? 'green.600'
                              : r.value < 0
                                ? 'red.500'
                                : 'gray.500'
                          }>
                          {r.value >= 0 ? '+' : ''}
                          {formatCurrency(r.value, true)}
                        </Text>
                        <Text fontSize="2xs" color="gray.400">
                          ({r.count})
                        </Text>
                      </HStack>
                    </HStack>
                  ))}
                </VStack>

                {/* Total */}
                <HStack mb={2} justify="space-between" px={1}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600">
                    {t('Total ({{count}} items)', { count: filtered.length })}
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={
                      totalValue > 0
                        ? 'green.600'
                        : totalValue < 0
                          ? 'red.500'
                          : 'gray.500'
                    }>
                    {totalValue >= 0 ? '+' : ''}
                    {formatCurrency(totalValue, true)}
                  </Text>
                </HStack>

                {/* Detail list — grouped by product */}
                {productGroups.length > 0 ? (
                  <VStack spacing={0} align="stretch">
                    {productGroups.map((g) => {
                      const isExpanded = expandedProductId === g.product_id;
                      return (
                        <Box key={g.product_id}>
                          {/* Group header row */}
                          <HStack
                            w="full"
                            justify="space-between"
                            py={1.5}
                            px={1}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            align="center"
                            cursor="pointer"
                            _hover={{ bg: 'gray.50' }}
                            onClick={() =>
                              setExpandedProductId(
                                isExpanded ? null : g.product_id,
                              )
                            }>
                            <Icon
                              as={isExpanded ? FaChevronDown : FaChevronRight}
                              boxSize={3}
                              color="gray.400"
                              flexShrink={0}
                            />
                            <Image
                              src={
                                storeProductMap.get(g.product_id)?.image ||
                                defaultImage
                              }
                              alt={g.product_name}
                              boxSize="40px"
                              objectFit="cover"
                              borderRadius="md"
                              flexShrink={0}
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.src = defaultImage;
                              }}
                            />
                            <VStack
                              spacing={0}
                              align="flex-start"
                              flex={1}
                              minW={0}>
                              <Text fontSize="sm" noOfLines={1}>
                                {g.product_name}
                              </Text>
                              <Text
                                fontSize="xs"
                                color={
                                  g.totalQty > 0
                                    ? 'green.600'
                                    : g.totalQty < 0
                                      ? 'red.500'
                                      : 'gray.500'
                                }>
                                {(() => {
                                  const bd = formatInventoryBreakdown(
                                    g.totalQty,
                                    storeProductMap.get(g.product_id),
                                    { showSign: true, showSeparator: true },
                                  );
                                  return bd.text ? ` ${bd.text} ` : ' ';
                                })()}
                              </Text>
                            </VStack>
                            <VStack spacing={0} align="flex-end" flexShrink={0}>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                whiteSpace="nowrap"
                                color={
                                  g.totalValue > 0
                                    ? 'green.600'
                                    : g.totalValue < 0
                                      ? 'red.500'
                                      : 'gray.500'
                                }>
                                {g.totalValue >= 0 ? '+' : ''}
                                {formatCurrency(g.totalValue, true)}
                              </Text>
                              <Text fontSize="2xs" color="gray.400">
                                {t('{{count}} changes', {
                                  count: g.changes.length,
                                })}
                              </Text>
                            </VStack>
                          </HStack>

                          {/* Expanded: individual changes */}
                          <Collapse in={isExpanded} animateOpacity>
                            <VStack
                              spacing={0}
                              align="stretch"
                              bg="gray.50"
                              pl={6}
                              pr={1}>
                              {g.changes.map((c) => {
                                const val = changeValue(c);
                                const qty = c.change_quantity;
                                return (
                                  <HStack
                                    key={c.id}
                                    w="full"
                                    justify="space-between"
                                    py={1.5}
                                    px={1}
                                    borderBottom="1px solid"
                                    borderColor="gray.100"
                                    align="flex-start">
                                    <VStack
                                      spacing={0}
                                      align="flex-start"
                                      flex={1}
                                      minW={0}>
                                      <Text
                                        fontSize="xs"
                                        color={
                                          qty > 0
                                            ? 'green.600'
                                            : qty < 0
                                              ? 'red.500'
                                              : 'gray.500'
                                        }>
                                        {(() => {
                                          const bd = formatInventoryBreakdown(
                                            c.change_quantity,
                                            storeProductMap.get(c.product_id),
                                            {
                                              showSign: true,
                                              showSeparator: true,
                                            },
                                          );
                                          return bd.text ? ` ${bd.text} ` : ' ';
                                        })()}
                                      </Text>
                                      <Badge
                                        fontSize="2xs"
                                        variant="subtle"
                                        colorScheme="gray">
                                        {getReasonLabel(t)[c.reason] ??
                                          c.reason}
                                      </Badge>
                                    </VStack>
                                    <VStack
                                      spacing={0}
                                      align="flex-end"
                                      flexShrink={0}>
                                      <Text
                                        fontSize="xs"
                                        fontWeight="bold"
                                        whiteSpace="nowrap"
                                        color={
                                          val > 0
                                            ? 'green.600'
                                            : val < 0
                                              ? 'red.500'
                                              : 'gray.500'
                                        }>
                                        {val >= 0 ? '+' : ''}
                                        {formatCurrency(val, true)}
                                      </Text>
                                      <Text fontSize="2xs" color="gray.400">
                                        {formatDate(
                                          new Date(c.created_at),
                                          'dd MMM HH:mm',
                                        )}
                                      </Text>
                                      <Text
                                        fontSize="2xs"
                                        color="gray.400"
                                        noOfLines={1}>
                                        {c.source_name}
                                      </Text>
                                    </VStack>
                                  </HStack>
                                );
                              })}
                            </VStack>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </VStack>
                ) : (
                  <Text color="gray.500" textAlign="center" py={8}>
                    {t('No inventory changes for this period')}
                  </Text>
                )}
              </TabPanel>

              {/* ── Tab 2: Daily Counts ── */}
              {hasDailyCounts && (
                <TabPanel px={3} py={2}>
                  <DailyCountsPanel
                    dailyCountLists={dailyCountLists!}
                    storeProductMap={storeProductMap}
                  />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/* ── Daily Counts Panel ── */

const getStatusConfig = (
  t: SharedUiT,
): Record<DailyCountItemStatus, { label: string; color: string }> => ({
  [DailyCountItemStatus.COUNTED]: { label: t('Counted'), color: 'green' },
  [DailyCountItemStatus.PENDING]: { label: t('Pending'), color: 'yellow' },
  [DailyCountItemStatus.CARRIED_OVER]: {
    label: t('Carried Over'),
    color: 'purple',
  },
});

function completionColor(pct: number): string {
  if (pct < 50) return 'red';
  if (pct < 90) return 'orange';
  return 'green';
}

function DailyCountsPanel({
  dailyCountLists,
  storeProductMap,
}: {
  dailyCountLists: DailyCountList[];
  storeProductMap: Map<string, StoreProduct>;
}) {
  const { t } = useSharedUiTranslation();
  const statusConfig = getStatusConfig(t);
  const sorted = useMemo(
    () =>
      [...dailyCountLists].sort(
        (a, b) =>
          new Date(b.count_date).getTime() - new Date(a.count_date).getTime(),
      ),
    [dailyCountLists],
  );

  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const toggleDate = (date: string) => {
    setExpandedDate((prev) => (prev === date ? null : date));
    setExpandedItem(null);
  };

  const toggleItem = (key: string) => {
    setExpandedItem((prev) => (prev === key ? null : key));
  };

  return (
    <VStack spacing={1} align="stretch">
      {sorted.map((list) => {
        const counted = list.items.filter(
          (i) => i.status === DailyCountItemStatus.COUNTED,
        ).length;
        const target = list.target_count || list.items.length || 1;
        const pct = Math.round((counted / target) * 100);
        const color = completionColor(pct);
        const isExpanded = expandedDate === list.count_date;

        return (
          <Box key={list.count_date}>
            {/* Date row */}
            <HStack
              px={2}
              py={2}
              bg="gray.50"
              borderRadius="md"
              cursor="pointer"
              _hover={{ bg: 'gray.100' }}
              onClick={() => toggleDate(list.count_date)}>
              <Icon
                as={isExpanded ? FaChevronDown : FaChevronRight}
                boxSize={3}
                color="gray.400"
              />
              <Text fontSize="sm" fontWeight="medium" flex={1}>
                {formatDate(
                  new Date(list.count_date + 'T00:00:00'),
                  'dd MMM yyyy',
                )}
              </Text>
              <Badge
                colorScheme={color}
                fontSize="2xs"
                px={2}
                borderRadius="full">
                {t('{{counted}}/{{target}} counted ({{percent}}%)', {
                  counted,
                  target,
                  percent: pct,
                })}
              </Badge>
            </HStack>

            {/* Expanded: items for this date */}
            <Collapse in={isExpanded} animateOpacity>
              <VStack spacing={0} align="stretch" pl={2} mt={1}>
                {list.items.map((item) => {
                  const product = storeProductMap.get(item.product_id);
                  const fractions = product
                    ? [...(product.store_product_fractions ?? []), product]
                    : [];
                  const itemKey = `${list.count_date}-${item.product_id}`;
                  const isItemExpanded = expandedItem === itemKey;
                  const hasAudit =
                    item.count_audit && item.count_audit.length > 0;
                  const statusCfg =
                    statusConfig[item.status] ?? statusConfig.PENDING;

                  return (
                    <Box key={item.product_id}>
                      <HStack
                        py={1.5}
                        px={1}
                        borderBottom="1px solid"
                        borderColor="gray.100"
                        align="center"
                        cursor={hasAudit ? 'pointer' : 'default'}
                        _hover={hasAudit ? { bg: 'gray.50' } : undefined}
                        onClick={() => hasAudit && toggleItem(itemKey)}>
                        <Image
                          src={product?.image || defaultImage}
                          alt={item.product_name}
                          boxSize="36px"
                          objectFit="cover"
                          borderRadius="md"
                          flexShrink={0}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = defaultImage;
                          }}
                        />
                        <VStack
                          spacing={0}
                          align="flex-start"
                          flex={1}
                          minW={0}>
                          <Text fontSize="xs" noOfLines={1} fontWeight="medium">
                            {item.product_name}
                          </Text>
                          <HStack spacing={1}>
                            <Badge
                              colorScheme={statusCfg.color}
                              fontSize="2xs"
                              variant="subtle">
                              {statusCfg.label}
                            </Badge>
                            {item.carried_from_date && (
                              <Text fontSize="2xs" color="gray.400">
                                {t('from {{date}}', {
                                  date: formatDate(
                                    new Date(
                                      item.carried_from_date + 'T00:00:00',
                                    ),
                                    'dd MMM',
                                  ),
                                })}
                              </Text>
                            )}
                          </HStack>
                        </VStack>
                        <VStack spacing={0} align="flex-end" flexShrink={0}>
                          {item.status === DailyCountItemStatus.COUNTED ? (
                            <>
                              <HStack spacing={1} align="baseline">
                                <Text fontSize="2xs" color="gray.400">
                                  {t('Exp:')}
                                </Text>
                                <InventoryBreakdown
                                  inventory={item.expected_stock ?? 0}
                                  fractions={fractions}
                                  parentUnit={product?.unit}
                                  fontSize="xs"
                                  showInStockText={false}
                                />
                              </HStack>
                              <HStack spacing={1} align="baseline">
                                <Text fontSize="2xs" color="gray.400">
                                  {t('Got:')}
                                </Text>
                                <InventoryBreakdown
                                  inventory={item.counted_stock ?? 0}
                                  fractions={fractions}
                                  parentUnit={product?.unit}
                                  fontSize="xs"
                                  fontWeight="bold"
                                  showInStockText={false}
                                />
                              </HStack>
                            </>
                          ) : (
                            <HStack spacing={1} align="baseline">
                              <Text fontSize="2xs" color="gray.400">
                                {t('Exp:')}
                              </Text>
                              <InventoryBreakdown
                                inventory={item.expected_stock ?? 0}
                                fractions={fractions}
                                parentUnit={product?.unit}
                                fontSize="xs"
                                showInStockText={false}
                              />
                            </HStack>
                          )}
                          {hasAudit && (
                            <HStack spacing={0.5} mt={0.5}>
                              <Icon
                                as={FaHistory}
                                boxSize={2.5}
                                color="blue.400"
                              />
                              <Text fontSize="2xs" color="blue.400">
                                {t('{{count}} audits', {
                                  count: item.count_audit!.length,
                                })}
                              </Text>
                            </HStack>
                          )}
                        </VStack>
                      </HStack>

                      {/* Audit trail */}
                      {hasAudit && (
                        <Collapse in={isItemExpanded} animateOpacity>
                          <VStack
                            spacing={1}
                            align="stretch"
                            bg="blue.50"
                            px={3}
                            py={2}
                            ml={4}
                            mr={1}
                            borderRadius="md"
                            mb={1}>
                            <Text
                              fontSize="2xs"
                              fontWeight="bold"
                              color="blue.600">
                              {t('Audit Trail')}
                            </Text>
                            {item
                              .count_audit!.sort(
                                (a, b) =>
                                  new Date(b.counted_at).getTime() -
                                  new Date(a.counted_at).getTime(),
                              )
                              .map((audit, idx) => {
                                const auditProduct = storeProductMap.get(
                                  item.product_id,
                                );
                                const auditFractions = auditProduct
                                  ? [
                                      ...(auditProduct.store_product_fractions ??
                                        []),
                                      auditProduct,
                                    ]
                                  : [];
                                return (
                                  <Box
                                    key={idx}
                                    bg="white"
                                    px={2}
                                    py={1.5}
                                    borderRadius="sm"
                                    borderLeft="3px solid"
                                    borderColor="blue.300">
                                    <HStack
                                      justify="space-between"
                                      align="flex-start">
                                      <VStack spacing={0} align="flex-start">
                                        <Text
                                          fontSize="2xs"
                                          fontWeight="medium">
                                          {audit.counted_by_name}
                                        </Text>
                                        <Text fontSize="2xs" color="gray.400">
                                          {formatDate(
                                            new Date(audit.counted_at),
                                            'dd MMM HH:mm',
                                          )}
                                        </Text>
                                        {audit.recount_reason && (
                                          <Text
                                            fontSize="2xs"
                                            color="orange.600"
                                            fontStyle="italic">
                                            {t('Reason: {{reason}}', {
                                              reason: audit.recount_reason,
                                            })}
                                          </Text>
                                        )}
                                      </VStack>
                                      <VStack spacing={0} align="flex-end">
                                        <HStack spacing={1} align="baseline">
                                          <Text fontSize="2xs" color="gray.400">
                                            {t('Exp:')}
                                          </Text>
                                          <InventoryBreakdown
                                            inventory={audit.expected_stock}
                                            fractions={auditFractions}
                                            parentUnit={auditProduct?.unit}
                                            fontSize="2xs"
                                            showInStockText={false}
                                          />
                                        </HStack>
                                        <HStack spacing={1} align="baseline">
                                          <Text fontSize="2xs" color="gray.400">
                                            {t('Got:')}
                                          </Text>
                                          <InventoryBreakdown
                                            inventory={audit.counted_stock}
                                            fractions={auditFractions}
                                            parentUnit={auditProduct?.unit}
                                            fontSize="2xs"
                                            fontWeight="bold"
                                            showInStockText={false}
                                          />
                                        </HStack>
                                      </VStack>
                                    </HStack>
                                  </Box>
                                );
                              })}
                          </VStack>
                        </Collapse>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            </Collapse>
          </Box>
        );
      })}
    </VStack>
  );
}
